import { secondsToMilliseconds } from 'date-fns';
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { IdleModalContainer } from 'play/organisms/idle-modal/IdleModalContainer';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';

export interface IdleTrackerProps {
  /**
   * Consider user idle after <timeout> ms
   */
  idleTimeout: number;

  /**
   * Show idle modal for <duration> ms
   */
  modalDuration: number;

  onAction?(): void;
  onExit?(): void;
  onExpire?(): void;
}

export const IdleTracker = ({ idleTimeout, modalDuration, onAction, onExit, onExpire }: IdleTrackerProps) => {
  const [idleModalOpen, setIdleModalOpen] = useState(false);
  const [msRemaining, setMsRemaining] = useState(0);
  const trackAmplitudeEvent = useTrackAmplitudeEvent();

  const { activate, getRemainingTime } = useIdleTimer({
    // Consider user as idle after <timeout> ms
    timeout: idleTimeout,

    // Show idle pop-up <promptBeforeIdle> ms before timeout
    promptBeforeIdle: modalDuration,

    // Trigger at most one onAction every <throttle> ms
    throttle: secondsToMilliseconds(5),

    // Stops triggering onAction events after onIdle is called
    stopOnIdle: true,

    // Time to show the modal
    onPrompt: () => {
      trackAmplitudeEvent({ name: 'Lab - Idle - Popup displayed' });
      setIdleModalOpen(true);
    },

    // Modal expired
    onIdle: () => {
      setIdleModalOpen(false);
      onExpire?.();
    },

    // User dismissed modal
    onActive: onAction,

    // User action
    onAction: () => {
      if (!idleModalOpen) {
        onAction?.();
      }
    },
  });

  // Ticker for countdown
  useEffect(() => {
    const intervalId = setInterval(() => setMsRemaining(getRemainingTime()), 1000);
    return () => clearInterval(intervalId);
  }, [getRemainingTime]);

  return (
    <IdleModalContainer
      msRemaining={msRemaining}
      onClose={() => {
        trackAmplitudeEvent({ name: 'Lab - Idle - User still there' });
        // Restarts onAction events
        activate();
        setIdleModalOpen(false);
      }}
      onExit={onExit}
      open={idleModalOpen}
    />
  );
};
